import { useCallback, useEffect, useState } from 'react';
import {
  PendingUpload,
  PENDING_UPLOAD_KEY,
  read,
  clear,
  isExpired,
  migrateLegacyKeys,
} from './pendingUploadStore';

/**
 * Boot-time hook that surfaces a persisted in-progress upload (if any) so
 * the host page can render a resume offer.
 *
 * Behaviour:
 *  - Reads `pendingUploadStore` on mount.
 *  - If the record has aged past the 6.5-day expiry (under the 7-day GCS URL
 *    TTL), auto-clears it and returns null.
 *  - Subscribes to `storage` events so a clear from another tab (e.g. user
 *    completed an upload elsewhere) is reflected here.
 *  - Returns `null` during SSR and during the first client render before the
 *    storage read settles, to avoid hydration mismatch.
 */
export interface UsePendingUploadResult {
  /** The persisted record, or null if absent / expired / pre-hydration. */
  pendingUpload: PendingUpload | null;
  /** True once the initial read has run on the client. */
  ready: boolean;
  /** Manually clear the record (e.g. user dismissed the resume offer). */
  clearPendingUpload: () => void;
}

export const usePendingUpload = (): UsePendingUploadResult => {
  const [pendingUpload, setPendingUpload] = useState<PendingUpload | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // One-shot: drop legacy keys from the pre-resume Upload.ts bookkeeping.
    migrateLegacyKeys();

    const record = read();
    if (record && isExpired(record)) {
      clear();
      setPendingUpload(null);
    } else {
      setPendingUpload(record);
    }
    setReady(true);
  }, []);

  // Cross-tab sync: another tab clearing or writing the record updates us.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== PENDING_UPLOAD_KEY && e.key !== null) return;
      // e.key === null fires on storage.clear(), refresh either way.
      const record = read();
      if (record && isExpired(record)) {
        clear();
        setPendingUpload(null);
      } else {
        setPendingUpload(record);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clearPendingUpload = useCallback(() => {
    clear();
    setPendingUpload(null);
  }, []);

  return { pendingUpload, ready, clearPendingUpload };
};
